// ** React Imports
import { Ref, useState, forwardRef, ReactElement, FC } from "react";

// ** MUI Imports
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import Fade, { FadeProps } from "@mui/material/Fade";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import * as Yup from "yup";

// ** Icons Imports
import Close from "mdi-material-ui/Close";
import ChatQuestion from "mdi-material-ui/ChatQuestion";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// import { getAdmins, postCreateNewAdmin } from '../../store/apps/adminsSlice'
import * as Api from "../../../service";
import toast from "react-hot-toast";
import { useAppDispatch } from "../../../store";
import Textarea from "../../forms/form-elements/textarea";
import { updateFAQ } from "../../../store/apps/faqSlice";
import { Checkbox, FormControlLabel, MenuItem, Select } from "@mui/material";

const Transition = forwardRef(function Transition(props: FadeProps & { children?: ReactElement<any, any> }, ref: Ref<unknown>) {
  return <Fade ref={ref} {...props} />;
});

interface AddQuestionFormInputs {
  readonly titleEN: string;
  readonly titleGE: string;
  readonly descriptionEN: string;
  readonly descriptionGE: string;
  readonly active: boolean;
  readonly forType: number;
}

const addQuestionFormSchema = Yup.object()
  .shape({
    titleEN: Yup.string().trim().min(3).required(),
    titleGE: Yup.string().trim().min(3).required(),
    descriptionEN: Yup.string().trim().min(3).required(),
    descriptionGE: Yup.string().trim().min(3).required(),
    forType: Yup.number(),
  })
  .required();

const DialogAddQuestion: FC<{
  onSuccess: () => void;
}> = (props) => {
  const [show, setShow] = useState<boolean>(false);
  const [forType, setForType] = useState<string>("0");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AddQuestionFormInputs>({
    resolver: yupResolver(addQuestionFormSchema),
  });

  const dispatch = useAppDispatch();

  const onSubmit: SubmitHandler<AddQuestionFormInputs> = (data) => {
    dispatch(updateFAQ({ ...data, forType: Number(forType) }))
      .unwrap()
      .then(() => {
        toast.success("Question Created successfully");
        reset();
        props.onSuccess();
        setShow(false);
      })
      .catch((response) => {
        toast.error(response.error);
      });
  };

  return (
    <Card>
      <CardContent sx={{ textAlign: "center" }}>
        <ChatQuestion sx={{ mb: 2, fontSize: "2rem" }} />
        <Typography variant="h6" sx={{ mb: 4 }}>
          Add New Question
        </Typography>
        {/* <Typography sx={{ mb: 3 }}>Use this modal to modify the existing user&prime;s current information.</Typography> */}
        <Button variant="contained" onClick={() => setShow(true)}>
          Add
        </Button>
      </CardContent>
      <Dialog
        fullWidth
        open={show}
        maxWidth="md"
        scroll="body"
        onClose={() => {
          reset();
          setShow(false);
        }}
        TransitionComponent={Transition}
        onBackdropClick={() => {
          reset();
          setShow(false);
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate spellCheck="false" autoComplete="off">
          <DialogContent
            sx={{
              pb: 6,
              px: { xs: 8, sm: 15 },
              pt: { xs: 8, sm: 12.5 },
              position: "relative",
            }}
          >
            <IconButton
              size="small"
              onClick={() => {
                reset();
                setShow(false);
              }}
              sx={{ position: "absolute", right: "1rem", top: "1rem" }}
            >
              <Close />
            </IconButton>
            <Box sx={{ mb: 8, textAlign: "center" }}>
              <Typography variant="h5" sx={{ mb: 3, lineHeight: "2rem" }}>
                Add New Question
              </Typography>
              {/* <Typography variant='body2'>Updating user details will receive a privacy audit.</Typography> */}
            </Box>
            <Grid container spacing={6} mb={5}>
              <Grid item sm={12} xs={12}>
                <Select value={forType} onChange={(e) => setForType(e.target.value)}>
                  <MenuItem value={`${Api.FAQEnum.general}`}>General</MenuItem>
                  <MenuItem value={`${Api.FAQEnum.employers}`}>Employers</MenuItem>
                  <MenuItem value={`${Api.FAQEnum.talents}`}>Talents</MenuItem>
                </Select>
              </Grid>
            </Grid>
            <Grid container spacing={6}>
              {/* <Grid item sm={12} xs={12}>
              <TextField fullWidth error={!!errors['email']} className='is-snvalid' label='Email' {...register("email")} />
            </Grid>
            <Grid item sm={12} xs={12}>
              <TextField fullWidth error={!!errors['name']} label='Name' {...register("name")} />
            </Grid>
            <Grid item sm={12} xs={12}> 
              <TextField fullWidth error={!!errors['lastName']} label='Last Name' {...register("lastName")} />
            </Grid> */}

              <Grid item sm={6} xs={12}>
                <TextField fullWidth label="Question EN" error={!!errors["titleEN"]} {...register("titleEN")} />
                <br />
                <br />
                <TextField multiline label="Answer EN" rows={4} error={!!errors["descriptionEN"]} {...register("descriptionEN")} fullWidth />
                <FormControlLabel control={<Checkbox {...register("active")} />} label="Active" />
              </Grid>
              <Grid item sm={6} xs={12}>
                <TextField fullWidth label="Question GE" error={!!errors["titleGE"]} {...register("titleGE")} />
                <br />
                <br />
                <TextField multiline label="Answer GE" rows={4} error={!!errors["descriptionGE"]} {...register("descriptionGE")} fullWidth />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ pb: { xs: 8, sm: 12.5 }, justifyContent: "center" }}>
            <Button
              type="submit"
              variant="contained"
              sx={{ mr: 2 }}
              // onClick={() => setShow(false)}
            >
              Add
            </Button>
            <Button
              variant="outlined"
              type="reset"
              color="secondary"
              onClick={() => {
                reset();
                setShow(false);
              }}
            >
              Discard
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Card>
  );
};

export default DialogAddQuestion;
